import React, { useState } from "react";
import {
  Copy,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Layout,
  Volume2,
  StopCircle,
} from "lucide-react";
import { notify } from "../../utils/toast";
import { Message } from "../../types/chat";
import { useSpeech } from "../../hooks/useSpeech";
import { createFeedback } from "../../services/api/messageApi";
// import { useAuth } from "../../hooks/chat/useAuth";
import {  getAuthData } from "../../contexts/AuthContext";

interface MessageActionsProps {
  message: Message;
  onRegenerate?: () => void;
  isSharedConversation?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  onRegenerate,
  isSharedConversation
}) => {
  const { speak, stopSpeaking, speaking, supported } = useSpeech();

  const [feedback, setFeedback] = useState<"like" | "dislike" | null>(
    message.metadata?.feedback || null
  );

  const handleCopy = () => {
    // Remove mermaid code blocks from the content before copying
    navigator.clipboard.writeText(message.content);
    notify.success("Message copied to clipboard");
  };

  const handleRegenerate = () => {
    if (onRegenerate) {
      notify.info("Regenerating response...");
      onRegenerate();
    }
  };

  const handleFeedback = async (rating: "like" | "dislike") => {
    try {
      const auth = await getAuthData();
      await createFeedback(auth, message.id, {
        walletAddress: auth.walletAddress,
        nonce: auth.nonce,
        user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn'),
        rating,
        content: rating === "like" ? "This was a good response!" : "This response was not good, accurate or efficient!",
      });

      setFeedback(rating);
      notify.success(
        rating === "like"
          ? "Response marked as helpful"
          : "Response marked as not helpful"
      );
    } catch (error) {
      console.error("Error submitting feedback:", error);
      notify.error("Failed to submit feedback");
    }
  };

  const handleLike = () => handleFeedback("like");
  const handleDislike = () => handleFeedback("dislike");

  const handleCanvas = () => {
    notify.info("Opening canvas view...");
  };

  const handleReadAloud = () => {
    if (speaking) {
      stopSpeaking();
    } else {
      // Remove mermaid code blocks from the content before reading aloud
      const cleanedContent = message.content.replace(/```mermaid[\s\S]*?```/g, 'Diagram Image.');
      speak(cleanedContent);
    }
  };

  // Hide actions if message is aborted
  const isAborted =
    message.sender === "assistant" &&
    message.content.includes("Agentic flow aborted!");

  if (isAborted) {
    return <div className="flex items-center justify-end space-x-2 mt-2 text-gray-500 dark:text-gray-400">
      <button
        onClick={handleRegenerate}
        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        title="Regenerate response"
      >
        <RefreshCw size={14} />
      </button>
    </div>;
  }
  return (
    <div className="flex items-center justify-end space-x-2 mt-2 text-gray-500 dark:text-gray-400">
      <button
        onClick={handleCopy}
        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        title="Copy message"
      >
        <Copy size={14} />
      </button>
      {!isSharedConversation && (
        <button
          onClick={handleRegenerate}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          title="Regenerate response"
        >
          <RefreshCw size={14} />
        </button>
      )}
      {!isSharedConversation && (
        <button
          onClick={handleLike}
          className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors ${
            feedback === "like" ? "text-green-600 dark:text-green-400" : ""
          }`}
          title="Mark as helpful"
        >
          <ThumbsUp size={14} />
        </button>
      )}
      {!isSharedConversation && (
        <button
          onClick={handleDislike}
          className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors ${
            feedback === "dislike" ? "text-red-600 dark:text-red-400" : ""
          }`}
          title="Mark as not helpful"
        >
          <ThumbsDown size={14} />
        </button>
      )}
      {/* <button
        onClick={handleCanvas}
        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
        title="Open in canvas"
      >
        <Layout size={14} />
      </button> */}
      {supported && (
        <button
          onClick={handleReadAloud}
          className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors ${
            speaking ? "text-blue-600 dark:text-blue-400" : ""
          }`}
          title={speaking ? "Stop reading" : "Read Response"}
        >
          {speaking ? <StopCircle size={14} /> : <Volume2 size={14} />}
        </button>
      )}
    </div>
  );
};
